/**
 * @description Table row component for a single recurring plan in the list view.
 *
 * @dependencies
 * - StatusBadge: Shared status indicator component
 * - PermissionGatedDropdown: Action menu with permission checks
 */

import { format } from 'date-fns';
import StatusBadge from '../shared/StatusBadge';
import { formatCurrency, parseDateSafely } from '../../utils/invoiceHelpers';
import { getCustomerDisplayName } from '../../utils/customerHelpers';

const FREQUENCY_LABELS = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly'
};

const FREQUENCY_PERIODS = {
  weekly: 'weeks',
  monthly: 'months',
  quarterly: 'quarters',
  yearly: 'years'
};

export default function RecurringPlanRow({ plan, onView }) {
  const customerName = getCustomerDisplayName({
    firstName: plan.customer_first_name,
    lastName: plan.customer_last_name,
    companyName: plan.customer_company_name
  }) || plan.customer_email || 'Unknown';

  const frequencyLabel = plan.interval_count > 1
    ? `Every ${plan.interval_count} ${FREQUENCY_PERIODS[plan.frequency] || plan.frequency}`
    : FREQUENCY_LABELS[plan.frequency] || plan.frequency;

  const nextChargeDate = plan.next_charge_date
    ? format(parseDateSafely(plan.next_charge_date), 'MMM d, yyyy')
    : '—';

  const cardInfo = plan.card_last_four
    ? `${plan.card_brand || 'Card'} ****${plan.card_last_four}`
    : 'No card';

  return (
    <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => onView(plan.id)}>
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <p className="text-sm font-medium text-gray-900">{customerName}</p>
          <p className="text-xs text-gray-500">{plan.description || 'Recurring Payment'}</p>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
        {formatCurrency(plan.amount_cents / 100)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
        {frequencyLabel}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
        {nextChargeDate}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm hidden lg:table-cell">
        <StatusBadge value={plan.status} type="recurring_status" />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">
        {cardInfo}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => onView(plan.id)}
            className="text-brand hover:text-brand-hover text-sm font-medium"
          >
            View
          </button>
        </div>
      </td>
    </tr>
  );
}
